import React, { useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { askAiQuestion } from "helpers/dashboard/fetchAiInsights";
import { formatAdvisorMessageContent } from "helpers/dashboard/formatAdvisorMessage";
import {
  playAdvisorReadySound,
  primeAdvisorReadySound,
} from "helpers/dashboard/playAdvisorReadySound";

const GREETING_MESSAGE =
  "Hello! Ask me anything. My creator Joe built me to be super smart when it comes to your website and business — he is cool like that — so ask away.";

const EXAMPLE_QUESTIONS = [
  "How healthy is enrollment overall right now?",
  "Which full classes should I add spots to first?",
  "Why are people interested in birthday parties but not registering?",
  "Where do we have open spots that need promotion?",
  "Which program page should I promote more on social media?",
  "Are families leaving the site before they find a class?",
];

const AdvisorIcon = () => (
  <svg
    className="owner-dashboard__advisor-icon"
    viewBox="0 0 24 24"
    aria-hidden="true"
    focusable="false"
  >
    <path
      fill="currentColor"
      d="M12 3C7.03 3 3 6.58 3 11c0 2.1.9 4.01 2.41 5.5L4 21l4.74-1.24A9.7 9.7 0 0 0 12 19c4.97 0 9-3.58 9-8s-4.03-8-9-8Zm0 14.5a8.2 8.2 0 0 1-4.16-1.13l-.3-.18-2.82.74.75-2.75-.2-.28A6.45 6.45 0 0 1 5.5 11C5.5 7.41 8.41 4.5 12 4.5S18.5 7.41 18.5 11 15.59 16.5 12 16.5Z"
    />
  </svg>
);

const TypingIndicator = () => (
  <li
    className="owner-dashboard__advisor-msg owner-dashboard__advisor-msg--assistant owner-dashboard__advisor-msg--typing"
    aria-label="Advisor is typing"
    role="status"
  >
    <div className="owner-dashboard__advisor-msg-bubble owner-dashboard__advisor-typing-bubble">
      <span className="owner-dashboard__advisor-typing-dots" aria-hidden="true">
        <span />
        <span />
        <span />
      </span>
    </div>
  </li>
);

const toConversationHistory = (messages) =>
  messages.map((message) => {
    if (message.role !== "assistant") {
      return { role: message.role, content: message.content };
    }

    const parts = [message.content];
    if (message.nextStep) {
      parts.push(`Next step: ${message.nextStep}`);
    }

    return { role: "assistant", content: parts.join("\n\n") };
  });

const MOBILE_ADVISOR_QUERY = "(max-width: 640px)";

const DashboardAdvisor = ({
  authToken,
  aiConfigured,
  activeView = "overview",
  isOpen: isOpenProp,
  onOpenChange,
}) => {
  const [isOpenInternal, setIsOpenInternal] = useState(false);
  const isOpen = isOpenProp ?? isOpenInternal;
  const setIsOpen = onOpenChange ?? setIsOpenInternal;

  const [draft, setDraft] = useState("");
  const [messages, setMessages] = useState([]);
  const [isAsking, setIsAsking] = useState(false);
  const [pendingQuestion, setPendingQuestion] = useState("");
  const [hasUnreadAnswer, setHasUnreadAnswer] = useState(false);
  const [error, setError] = useState("");
  const panelRef = useRef(null);
  const messagesEndRef = useRef(null);
  const isOpenRef = useRef(isOpen);
  const scrollLockYRef = useRef(0);
  const inputId = useId();
  const panelId = useId();

  useEffect(() => {
    isOpenRef.current = isOpen;
    if (isOpen) {
      setHasUnreadAnswer(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const isMobile = window.matchMedia(MOBILE_ADVISOR_QUERY).matches;
    if (!isMobile) return undefined;

    scrollLockYRef.current = window.scrollY;
    document.body.classList.add("owner-dashboard-advisor-locked");
    document.body.style.top = `-${scrollLockYRef.current}px`;
    document.documentElement.classList.add("owner-dashboard-advisor-locked");

    return () => {
      const scrollY = scrollLockYRef.current;
      document.body.classList.remove("owner-dashboard-advisor-locked");
      document.body.style.top = "";
      document.documentElement.classList.remove(
        "owner-dashboard-advisor-locked",
      );
      window.scrollTo(0, scrollY);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isAsking, isOpen, error]);

  useEffect(() => {
    if (!isOpen || isAsking) return undefined;

    const isMobile = () => window.matchMedia(MOBILE_ADVISOR_QUERY).matches;

    const handlePointerDown = (event) => {
      if (isMobile()) return;
      if (panelRef.current?.contains(event.target)) return;
      if (event.target.closest(".owner-dashboard__advisor-toggle")) return;
      if (event.target.closest(".owner-dashboard__advisor-topbar-btn")) return;
      setIsOpen(false);
    };

    const handleEscape = (event) => {
      if (event.key === "Escape") setIsOpen(false);
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen, isAsking, setIsOpen]);

  if (!aiConfigured || !authToken) {
    return null;
  }

  const hasUserMessages = messages.some((message) => message.role === "user");

  const handleNewChat = () => {
    if (isAsking) return;
    setMessages([]);
    setDraft("");
    setError("");
    setHasUnreadAnswer(false);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const trimmed = draft.trim();
    if (!trimmed || isAsking) return;

    const userMessage = {
      id: `user-${Date.now()}`,
      role: "user",
      content: trimmed,
    };
    const conversationHistory = toConversationHistory(messages);

    setMessages((prev) => [...prev, userMessage]);
    setDraft("");
    setIsAsking(true);
    setPendingQuestion(trimmed);
    setError("");
    setHasUnreadAnswer(false);
    primeAdvisorReadySound();

    try {
      const result = await askAiQuestion(
        authToken,
        trimmed,
        activeView,
        conversationHistory,
      );

      setMessages((prev) => [
        ...prev,
        {
          id: result.generatedAt || `assistant-${Date.now()}`,
          role: "assistant",
          content: result.answer,
          highlights: result.highlights || [],
          nextStep: result.nextStep || "",
        },
      ]);

      if (!isOpenRef.current) {
        setHasUnreadAnswer(true);
      }

      await playAdvisorReadySound();
    } catch (askError) {
      setError(askError.message || "Could not answer your question.");
    } finally {
      setIsAsking(false);
      setPendingQuestion("");
    }
  };

  const handleInputKeyDown = (event) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      handleSubmit(event);
    }
  };

  const handleScrimClick = () => {
    if (isAsking) return;
    setIsOpen(false);
  };

  const toggleLabel = (() => {
    if (isAsking) return "Working on your answer…";
    if (hasUnreadAnswer) return "Answer ready — tap to read";
    if (isOpen) return "Close advisor";
    return "Business advisor";
  })();

  const widget = (
    <>
      {isOpen && (
        <button
          type="button"
          className="owner-dashboard__advisor-scrim"
          aria-label="Close advisor"
          onClick={handleScrimClick}
          tabIndex={-1}
        />
      )}

      <div className="owner-dashboard__advisor">
        {isOpen && (
          <div
            ref={panelRef}
            id={panelId}
            className="owner-dashboard__advisor-panel owner-dashboard__advisor-panel--chat"
            role="dialog"
            aria-labelledby="dashboard-advisor-title"
            aria-modal="true"
          >
            <header className="owner-dashboard__advisor-header owner-dashboard__advisor-header--chat">
              <div className="owner-dashboard__advisor-header-side">
                {hasUserMessages && (
                  <button
                    type="button"
                    className="owner-dashboard__advisor-new-chat"
                    onClick={handleNewChat}
                    disabled={isAsking}
                  >
                    New chat
                  </button>
                )}
              </div>
              <div className="owner-dashboard__advisor-header-center">
                <h2
                  id="dashboard-advisor-title"
                  className="owner-dashboard__advisor-title"
                >
                  Business advisor
                </h2>
                <p className="owner-dashboard__advisor-status">
                  {isAsking ? "Typing…" : "Online"}
                </p>
              </div>
              <div className="owner-dashboard__advisor-header-side owner-dashboard__advisor-header-side--end">
                <button
                  type="button"
                  className="owner-dashboard__advisor-close"
                  onClick={() => setIsOpen(false)}
                  aria-label="Close advisor"
                >
                  ×
                </button>
              </div>
            </header>

            <div
              className="owner-dashboard__advisor-body owner-dashboard__advisor-body--chat"
              role="log"
              aria-live="polite"
              aria-relevant="additions"
            >
              <ul className="owner-dashboard__advisor-chat">
                <li className="owner-dashboard__advisor-msg owner-dashboard__advisor-msg--assistant">
                  <div className="owner-dashboard__advisor-msg-bubble">
                    <p className="owner-dashboard__advisor-msg-text">
                      {GREETING_MESSAGE}
                    </p>
                  </div>
                </li>

                {messages.map((message) => (
                  <li
                    key={message.id}
                    className={`owner-dashboard__advisor-msg owner-dashboard__advisor-msg--${message.role}`}
                  >
                    <div className="owner-dashboard__advisor-msg-bubble">
                      <p className="owner-dashboard__advisor-msg-text">
                        {message.role === "assistant"
                          ? formatAdvisorMessageContent(message.content)
                          : message.content}
                      </p>
                      {message.role === "assistant" &&
                        message.highlights?.length > 0 && (
                          <ul className="owner-dashboard__advisor-msg-highlights">
                            {message.highlights.map((line) => (
                              <li key={line}>
                                {formatAdvisorMessageContent(line)}
                              </li>
                            ))}
                          </ul>
                        )}
                      {message.role === "assistant" && message.nextStep && (
                        <p className="owner-dashboard__advisor-msg-next">
                          <strong>Next step:</strong>{" "}
                          {formatAdvisorMessageContent(message.nextStep)}
                        </p>
                      )}
                    </div>
                  </li>
                ))}

                {isAsking && <TypingIndicator />}
              </ul>

              {!hasUserMessages && !isAsking && (
                <ul
                  className="owner-dashboard__advisor-suggestions"
                  aria-label="Suggested questions"
                >
                  {EXAMPLE_QUESTIONS.map((example) => (
                    <li key={example}>
                      <button
                        type="button"
                        className="owner-dashboard__advisor-suggestion"
                        onClick={() => setDraft(example)}
                      >
                        {example}
                      </button>
                    </li>
                  ))}
                </ul>
              )}

              {isAsking && (
                <p className="owner-dashboard__advisor-background-note">
                  You can close this and keep using the dashboard — we&apos;ll
                  chime when your answer is ready.
                </p>
              )}

              {error && (
                <p
                  className="owner-dashboard__banner owner-dashboard__banner--error"
                  role="alert"
                >
                  {error}
                </p>
              )}

              <div ref={messagesEndRef} aria-hidden="true" />
            </div>

            <form
              className="owner-dashboard__advisor-compose"
              onSubmit={handleSubmit}
              aria-label="Send a message"
            >
              <label className="owner-dashboard__sr-only" htmlFor={inputId}>
                Message
              </label>
              <textarea
                id={inputId}
                className="owner-dashboard__advisor-compose-input"
                rows={1}
                value={draft}
                onChange={(event) => setDraft(event.target.value)}
                onKeyDown={handleInputKeyDown}
                placeholder="Message"
                disabled={isAsking}
                maxLength={500}
              />
              <button
                type="submit"
                className="owner-dashboard__advisor-compose-send"
                disabled={isAsking || !draft.trim()}
                aria-label="Send message"
              >
                <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                  <path
                    fill="currentColor"
                    d="M12 4l-1.41 1.41L16.17 11H4v2h12.17l-5.58 5.59L12 20l8-8-8-8z"
                  />
                </svg>
              </button>
            </form>
          </div>
        )}

        <button
          type="button"
          className={`owner-dashboard__advisor-toggle${
            isOpen ? " owner-dashboard__advisor-toggle--open" : ""
          }${isAsking ? " owner-dashboard__advisor-toggle--thinking" : ""}${
            hasUnreadAnswer ? " owner-dashboard__advisor-toggle--ready" : ""
          }`}
          onClick={() => setIsOpen(!isOpen)}
          aria-expanded={isOpen}
          aria-controls={panelId}
          aria-busy={isAsking}
        >
          <AdvisorIcon />
          {hasUnreadAnswer && !isOpen && (
            <span
              className="owner-dashboard__advisor-badge"
              aria-hidden="true"
            />
          )}
          <span className="owner-dashboard__advisor-toggle-text">
            <strong>{toggleLabel}</strong>
            {!isOpen && !isAsking && !hasUnreadAnswer && (
              <span>Chat about enrollment &amp; your website</span>
            )}
            {isAsking && !isOpen && pendingQuestion && (
              <span className="owner-dashboard__advisor-toggle-pending">
                {pendingQuestion}
              </span>
            )}
          </span>
        </button>
      </div>
    </>
  );

  return createPortal(widget, document.body);
};

export default DashboardAdvisor;
